import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';
import { supabase } from '../lib/supabase';

// Static iOS import - avoid dynamic-import races on iOS WKWebView where
// post-import microtask continuation has been observed to silently stall.
// Web path still uses dynamic import below.
import * as RCCapacitor from '@revenuecat/purchases-capacitor';

// RevenueCat integration shim.
//
// The actual SDK is loaded lazily so the rest of the app can render without
// the RC plugin being installed yet (the iOS plugin is added via
// @revenuecat/purchases-capacitor; web uses @revenuecat/purchases-js). Both
// expose a `Purchases` singleton with a similar surface - configure,
// logIn, getOfferings, purchasePackage, restorePurchases.
//
// SDK keys come from Vite env:
//   VITE_REVENUECAT_PUBLIC_KEY_IOS - used inside Capacitor iOS runtime
//   VITE_REVENUECAT_PUBLIC_KEY_WEB - used on the PWA / browser
//
// Purchase state is authoritative on the server - RC sends webhooks to the
// `revenuecat-webhook` edge function which updates `user_profiles.plan`.
// The client-side helpers here are *only* for purchase UX; never trust the
// client to decide entitlements.

const IS_NATIVE_IOS =
  Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios';

const SDK_KEY = IS_NATIVE_IOS
  ? import.meta.env.VITE_REVENUECAT_PUBLIC_KEY_IOS
  : import.meta.env.VITE_REVENUECAT_PUBLIC_KEY_WEB;

let Purchases = null;
let configurePromise = null;

// The Capacitor iOS `Purchases` reference is a registered-plugin Proxy that
// returns an auto-generated function for any property access - including
// `.then`. That makes the bare proxy look thenable to JS's `await`
// machinery: when an `async` function does `return Purchases`, the implicit
// Promise chaining notices the `.then` and tries to invoke it, which the
// native plugin then rejects with "Purchases.then() is not implemented on
// ios". Wrap the reference in a plain object so callers do
// `(await loadSdk()).sdk` and never await the proxy directly.
const loadSdk = async () => {
  if (Purchases) {
    console.log('[RevenueCat] loadSdk: cached');
    return { sdk: Purchases };
  }
  console.log('[RevenueCat] loadSdk start', { IS_NATIVE_IOS, hasKey: !!SDK_KEY });
  try {
    if (IS_NATIVE_IOS) {
      // Static import (resolved at top of module) - see import above.
      console.log('[RevenueCat] using static RCCapacitor');
      Purchases = RCCapacitor.Purchases || RCCapacitor.default;
      console.log('[RevenueCat] purchases-capacitor loaded', {
        hasPurchases: !!Purchases,
        modKeys: Object.keys(RCCapacitor),
      });
    } else {
      const mod = await import('@revenuecat/purchases-js');
      Purchases = mod.Purchases || mod.default;
    }
  } catch (err) {
    console.warn('[RevenueCat] SDK load failed:', err?.message || err);
    return { sdk: null };
  } finally {
    console.log('[RevenueCat] loadSdk finally', { hasPurchases: !!Purchases });
  }
  console.log('[RevenueCat] loadSdk returning');
  return { sdk: Purchases };
};

/**
 * Configure the SDK + identify the active Supabase user to RC. Safe to call
 * multiple times - subsequent calls reuse the same configuration promise.
 */
export const initRevenueCat = async () => {
  console.log('[RevenueCat] initRevenueCat invoked', { hasCachedPromise: !!configurePromise });
  if (configurePromise) return configurePromise;
  console.log('[RevenueCat] initRevenueCat start (fresh)');

  configurePromise = (async () => {
    const { sdk } = await loadSdk();
    if (!sdk) {
      console.warn('[RevenueCat] no sdk after loadSdk');
      return false;
    }
    if (!SDK_KEY) {
      console.warn('[RevenueCat] No SDK key configured for this platform');
      return false;
    }
    console.log('[RevenueCat] fetching supabase user');
    const { data: { user } } = await supabase.auth.getUser();
    console.log('[RevenueCat] supabase user', { hasUser: !!user, id: user?.id });
    if (!user) {
      console.warn('[RevenueCat] Not signed in; deferring configure');
      configurePromise = null;
      return false;
    }
    try {
      console.log('[RevenueCat] calling configure', { native: IS_NATIVE_IOS, keyPrefix: SDK_KEY?.slice(0, 8) });
      // Race the native configure() against a 10s timeout. iOS RC SDK has
      // been observed to hang the bridge call silently if the plugin isn't
      // fully registered; a timeout converts the hang into a diagnosable
      // error rather than a stuck UI.
      const configureCall = IS_NATIVE_IOS
        ? sdk.configure({ apiKey: SDK_KEY, appUserID: user.id })
        : sdk.configure(SDK_KEY, user.id);
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('configure() did not resolve within 10s')), 10000),
      );
      const result = await Promise.race([configureCall, timeoutPromise]);
      console.log('[RevenueCat] configure succeeded', { result });
      return true;
    } catch (err) {
      console.error('[RevenueCat] configure failed:', err?.message || err, err);
      configurePromise = null;
      return false;
    }
  })();

  return configurePromise;
};

// Re-identify after sign-in so RC's appUserID always matches Supabase's
// user_id. Called from AuthContext when the session changes.
export const identifyRevenueCat = async (userId) => {
  const { sdk } = await loadSdk();
  if (!sdk || !userId) return;
  await initRevenueCat();
  try {
    if (typeof sdk.logIn === 'function') {
      await sdk.logIn({ appUserID: userId });
    }
  } catch (err) {
    console.warn('[RevenueCat] logIn failed:', err?.message || err);
  }
};

/**
 * Fetch the configured offerings. Returns the "current" offering - typically
 * { monthly, annual } packages mapped to the same `pro` entitlement.
 */
// Method-surface differs between SDKs:
//   • purchases-js (web)       - instance method: Purchases.getSharedInstance().getOfferings()
//   • purchases-capacitor (iOS) - static method:   Purchases.getOfferings()
// We probe both so the same wrapper works on either platform without
// branching everywhere.
const callSdk = async (method, ...args) => {
  try {
    console.log('[RevenueCat] callSdk:enter', method);
    const { sdk } = await loadSdk();
    console.log('[RevenueCat] callSdk:loaded', method, { sdkExists: !!sdk, sdkType: typeof sdk });
    if (!sdk) {
      console.warn('[RevenueCat] callSdk: no sdk');
      return null;
    }
    console.log('[RevenueCat] callSdk:pre-init', method);
    const cfg = await initRevenueCat();
    console.log('[RevenueCat] callSdk:post-init', method, { cfg, hasSharedInstance: typeof sdk.getSharedInstance === 'function' });
    if (!IS_NATIVE_IOS && typeof sdk.getSharedInstance === 'function') {
      const inst = sdk.getSharedInstance();
      console.log('[RevenueCat] callSdk:web-instance', method);
      return inst[method](...args);
    }
    console.log('[RevenueCat] callSdk:native-static-call', method);
    const r = await sdk[method](...args);
    console.log('[RevenueCat] callSdk:returned', method, { hasResult: !!r });
    return r;
  } catch (err) {
    console.error('[RevenueCat] callSdk:THREW', method, err?.message || err, err);
    throw err;
  }
};

export const getOfferings = async () => {
  console.log('[RevenueCat] getOfferings called');
  try {
    if (IS_NATIVE_IOS) {
      // Inline everything for iOS - debugging shows async/await chain dies
      // somewhere in initRevenueCat. Drop all indirection to isolate.
      console.log('[RevenueCat] iOS path: synchronous configure attempt');
      const P = RCCapacitor.Purchases;
      console.log('[RevenueCat] P resolved', { hasP: !!P, hasConfigure: typeof P?.configure });
      const { data: { user } } = await supabase.auth.getUser();
      console.log('[RevenueCat] auth user', { hasUser: !!user, id: user?.id });
      if (!user) return null;
      console.log('[RevenueCat] about to call P.configure');
      const configurePromise = P.configure({ apiKey: SDK_KEY, appUserID: user.id });
      console.log('[RevenueCat] P.configure returned promise', { isPromise: configurePromise instanceof Promise });
      const cfg = await Promise.race([
        configurePromise,
        new Promise((_, r) => setTimeout(() => r(new Error('configure timeout 8s')), 8000)),
      ]);
      console.log('[RevenueCat] configure resolved', cfg);
      console.log('[RevenueCat] calling getOfferings');
      const offerings = await Promise.race([
        P.getOfferings(),
        new Promise((_, r) => setTimeout(() => r(new Error('getOfferings timeout 8s')), 8000)),
      ]);
      console.log('[RevenueCat] getOfferings result', {
        hasOfferings: !!offerings,
        keys: offerings ? Object.keys(offerings) : null,
      });
      return offerings?.current || null;
    }
    // Web path - keep working
    const ok = await initRevenueCat();
    if (!ok) return null;
    const { sdk } = await loadSdk();
    const offerings = await sdk.getSharedInstance().getOfferings();
    return offerings?.current || null;
  } catch (err) {
    console.error('[RevenueCat] getOfferings failed:', err?.message || err, err);
    return null;
  }
};

/**
 * Purchase a package and wait for the customer info update. The webhook
 * fires asynchronously to update Supabase plan state - callers should call
 * `refreshPlan()` from AuthContext after this resolves to pick up the new
 * entitlement.
 */
export const purchasePackage = async (rcPackage) => {
  const { sdk } = await loadSdk();
  if (!sdk) throw new Error('RevenueCat SDK not available');
  await initRevenueCat();
  if (!IS_NATIVE_IOS && typeof sdk.getSharedInstance === 'function') {
    // purchases-js: instance.purchase({ rcPackage, customerEmail }).
    // Passing the email is documented as optional but in practice RC
    // can't render Stripe Elements without a customer email tied to the
    // Stripe Customer record - checkout errors out with
    // "Failed to initialize payment form". Pull it from the active
    // Supabase session.
    const { data: { user } } = await supabase.auth.getUser();
    return sdk.getSharedInstance().purchase({
      rcPackage,
      customerEmail: user?.email,
    });
  }
  // purchases-capacitor: static.purchasePackage({ aPackage })
  return sdk.purchasePackage({ aPackage: rcPackage });
};

/**
 * Restore purchases (required by Apple Review). Surfaces any active
 * entitlements the user is owed but the client has forgotten about
 * (re-installs, account switches).
 */
export const restorePurchases = async () => {
  const { sdk } = await loadSdk();
  if (!sdk) throw new Error('RevenueCat SDK not available');
  await initRevenueCat();
  if (!IS_NATIVE_IOS && typeof sdk.getSharedInstance === 'function') {
    // purchases-js doesn't have explicit restore - the customer info is
    // fetched fresh by re-identifying. Match the same shape callers expect.
    return sdk.getSharedInstance().getCustomerInfo();
  }
  return sdk.restorePurchases();
};

export const isRevenueCatAvailable = () => !!SDK_KEY;

// True only inside the native iOS app (StoreKit purchases). Everywhere else -
// Mac/Chrome/Android/Safari browsers and the PWA - billing runs through
// RevenueCat Web Billing (Stripe), where Apple's cancellation path and EULA
// don't apply. Exported so UI copy can branch on the billing channel without
// re-deriving the platform check.
export const isNativeIOS = IS_NATIVE_IOS;

// Apple's stable URL for the user's auto-renewable subscriptions screen.
// Used as a fallback when RC's customerInfo.managementURL hasn't populated
// yet (e.g. immediately after a fresh purchase, before the next refresh).
const APPLE_SUBSCRIPTIONS_URL = 'https://apps.apple.com/account/subscriptions';

/**
 * Fetch the subscription management URL for the current user.
 *
 * RevenueCat's CustomerInfo carries a `managementURL`:
 *   • iOS purchases → https://apps.apple.com/account/subscriptions
 *   • Web (Stripe) purchases → an RC-hosted billing portal URL
 *   • null when the user has no active store-managed subscription
 *     (e.g. plan was granted manually, or RC hasn't seen the receipt yet).
 *
 * Returns null on any error / no-active-sub case so the caller can decide
 * how to degrade (show explanatory copy, hide button, etc.).
 */
export const getManagementUrl = async () => {
  try {
    const { sdk } = await loadSdk();
    if (!sdk) return null;
    const ok = await initRevenueCat();
    if (!ok) return null;
    let customerInfo;
    if (!IS_NATIVE_IOS && typeof sdk.getSharedInstance === 'function') {
      customerInfo = await sdk.getSharedInstance().getCustomerInfo();
    } else {
      customerInfo = await sdk.getCustomerInfo();
    }
    return customerInfo?.managementURL || customerInfo?.customerInfo?.managementURL || null;
  } catch (err) {
    console.warn('[RevenueCat] getManagementUrl failed:', err?.message || err);
    return null;
  }
};

/**
 * Open the subscription management surface for the current platform.
 *
 * Strategy:
 *   1. On native iOS, prefer the native subscription sheet via RC's
 *      `Purchases.showManageSubscriptions()` (SKPaymentQueue.showManageSubscriptions
 *      under the hood — iOS 15+ modal that stays inside the app).
 *   2. Otherwise fetch RC's `managementURL` (Stripe portal on web, App Store
 *      URL fallback on iOS) and open it via Capacitor Browser /
 *      `window.open`.
 *   3. Last-resort iOS fallback to the public App Store subscriptions URL.
 *   4. If we're on web and RC has nothing, return false so the caller can
 *      surface a "contact support" affordance.
 */
export const openManagementUrl = async () => {
  if (IS_NATIVE_IOS) {
    try {
      const { sdk } = await loadSdk();
      if (sdk?.showManageSubscriptions) {
        await initRevenueCat();
        await sdk.showManageSubscriptions();
        return true;
      }
    } catch (err) {
      console.warn('[RevenueCat] showManageSubscriptions failed, falling back to URL:', err?.message || err);
    }
  }

  let url = await getManagementUrl();
  if (!url && IS_NATIVE_IOS) {
    url = APPLE_SUBSCRIPTIONS_URL;
  }
  if (!url) return false;
  try {
    await Browser.open({ url });
  } catch (err) {
    console.warn('[RevenueCat] Browser.open failed, falling back to window.open:', err?.message || err);
    if (typeof window !== 'undefined') {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  }
  return true;
};
